/**
 * Helpers for building and rendering the live agent "timeline" — an ordered list of
 * intermediate reasoning ("thought") blocks and tool-call steps. Keeping them in
 * chronological order lets the UI interleave thoughts and tool calls the way ChatGPT
 * and Copilot do (a thought that follows a tool call renders below it).
 */
import { ChatMessage, FunctionCall, TimelineItem } from "../interfaces/copilot-chat.interfaces";
import { AgentStreamStepDto } from "./copilotStream";

let _thoughtSeq = 0;

/**
 * Append a thought delta to the timeline. Consecutive thoughts from the same agent are
 * merged into the same block (so streamed token deltas grow one bubble); a new block is
 * started when the source changes or a step has arrived since the last thought.
 */
export const appendThoughtToTimeline = (
  timeline: TimelineItem[] | undefined,
  text: string,
  source?: string
): TimelineItem[] => {
  const next = timeline ? [...timeline] : [];
  const last = next[next.length - 1];
  if (last && last.kind === "thought" && last.source === source) {
    next[next.length - 1] = { ...last, text: last.text + text };
  } else {
    next.push({ kind: "thought", id: `t${++_thoughtSeq}`, source, text });
  }
  return next;
};

/**
 * Insert or update a tool step in the timeline (matched by callId), preserving order.
 */
export const mergeStepIntoTimeline = (
  timeline: TimelineItem[] | undefined,
  step: FunctionCall
): TimelineItem[] => {
  const next = timeline ? [...timeline] : [];
  const idx = next.findIndex((it) => it.kind === "step" && it.step.callId === step.callId);
  if (idx >= 0) {
    const existing = next[idx] as { kind: "step"; step: FunctionCall };
    // A later lifecycle update (e.g. "success") carries the output (content) but not
    // the arguments; a "running" update carries the arguments but no content. Merge so
    // both the input (arguments) and output (content) are retained.
    next[idx] = {
      kind: "step",
      step: {
        ...existing.step,
        ...step,
        arguments: step.arguments && step.arguments.length > 0 ? step.arguments : existing.step.arguments,
        content: step.content != null ? step.content : existing.step.content,
      },
    };
  } else {
    next.push({ kind: "step", step });
  }
  return next;
};

/**
 * Apply a streamed tool-step lifecycle update to a chat message: upsert it into both the
 * flat `functionCalls` list (used by the completed "Steps" tab) and the ordered `timeline`
 * (used for the live interleaved view). A later update (e.g. "success") carries the output
 * but not the arguments, and vice-versa, so existing input/output are preserved on merge.
 */
export const applyStepToMessage = (m: ChatMessage, step: AgentStreamStepDto): ChatMessage => {
  const fc: FunctionCall = {
    callId: step.callId,
    name: step.name,
    content: step.content,
    isError: step.isError ?? step.status === "error",
    arguments: step.arguments || [],
    status: step.status,
    source: step.source,
  };
  const existing = m.functionCalls ? [...m.functionCalls] : [];
  const idx = existing.findIndex((f) => f.callId === fc.callId);
  if (idx >= 0) {
    const prev = existing[idx];
    existing[idx] = {
      ...prev,
      ...fc,
      arguments: fc.arguments && fc.arguments.length > 0 ? fc.arguments : prev.arguments,
      content: fc.content != null ? fc.content : prev.content,
    };
  } else {
    existing.push(fc);
  }
  return { ...m, functionCalls: existing, timeline: mergeStepIntoTimeline(m.timeline, fc) };
};

/**
 * A short, human-friendly label for the agent that produced a thought.
 */
export const friendlySource = (source?: string): string => {
  switch (source) {
    case "PlanningAgent":
      return "Planning";
    case "context_builder_agent":
      return "Gathering context";
    default:
      return "Thinking";
  }
};

/**
 * Join all thought blocks in a timeline into a single string (used for the collapsed
 * "Thoughts" view on a completed message).
 */
export const timelineThoughtsText = (timeline?: TimelineItem[]): string =>
  (timeline || [])
    .filter((it): it is Extract<TimelineItem, { kind: "thought" }> => it.kind === "thought")
    .map((it) => it.text)
    .join("\n\n")
    .trim();
